/**
 * TanStack Query hooks for surfacer decision persistence.
 *
 * Reads from `/api/reader/cohort/<cohort>/study/<uid>/surfacer/decisions` and
 * writes via `/api/reader/cohort/<cohort>/study/<uid>/surfacer/decision`.
 * The backend stores idempotent PutItem rows in `a2z-medi-flow`; pending is
 * a stored value, not an absence of row. The frontend treats absent and
 * pending identically.
 *
 * A row may also carry the reader's `actionable` call and a short `note`. Each
 * write replaces the whole row, so every POST sends the row's full intended
 * state, and writes for one study run one at a time (mutation `scope`) so a
 * late response cannot reorder them. A queued write has already applied its
 * row to the cache, so a failed earlier write rolls back only when nothing is
 * queued behind it, and the study is re-read once its writes drain. The pooled
 * cohort review state (`useSurfacerReviewState`) is re-read then too.
 */

import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { surfacerReviewStateKeys } from "./useSurfacerReviewState";

export type Verdict =
  | "true_miss"
  | "inconsequential"
  | "wrong_name"
  | "report_mentions"
  | "other_report"
  | "overcall"
  | "ambiguous"
  | "pending";

export type DecisionsMap = Record<string, Verdict>;

/** Verdicts that take an actionable call and a note. Any other verdict is
 * written without them, which clears them on the row. */
export const ACTIONABILITY_VERDICTS: ReadonlySet<Verdict> = new Set<Verdict>([
  "true_miss",
  "inconsequential",
]);

/** Mirrors the backend `NOTE_MAX_CHARS` (src/surfacer_decisions.py), which is
 * what enforces it; this only stops the input before the backend would 400. */
export const NOTE_MAX_CHARS = 1000;

/** Optional per-finding detail; a key is present only when the row stores it. */
export interface DecisionDetails {
  actionable?: boolean;
  note?: string;
}

export type DetailsMap = Record<string, DecisionDetails>;

/** Per-study cache entry: verdicts plus the details of rows that have any. */
export interface StudyDecisions {
  decisions: DecisionsMap;
  details: DetailsMap;
}

/**
 * All of the caller's verdicts, keyed cohort_id → study_uid → {finding: verdict}.
 * Nested by cohort (since the versioned-key change) so the same study reviewed
 * under two reporter-version replay cohorts stays distinct — join per cohort.
 */
export type AllDecisionsMap = Record<string, Record<string, DecisionsMap>>;

interface DecisionsResponse {
  decisions: DecisionsMap;
  details?: DetailsMap;
}

interface AllDecisionsResponse {
  decisions: AllDecisionsMap;
}

const SURFACER_DECISIONS_ROOT = ["surfacer-decisions"] as const;

export const surfacerDecisionKeys = {
  all: SURFACER_DECISIONS_ROOT,
  study: (studyUid: string, cohortId: string) =>
    [...SURFACER_DECISIONS_ROOT, "study", cohortId, studyUid] as const,
  // Bulk cross-study key — one cache entry backs the whole worklist, shared
  // across single-cohort and merged multi-cohort views.
  allStudies: [...SURFACER_DECISIONS_ROOT, "all-studies"] as const,
};

async function fetchDecisions(cohortId: string, studyUid: string): Promise<StudyDecisions> {
  const baseUrl = getApiBaseUrl();
  const encodedCohort = encodeURIComponent(cohortId);
  const encodedUid = encodeURIComponent(studyUid);
  const response = await axios.get<DecisionsResponse>(
    `${baseUrl}/api/reader/cohort/${encodedCohort}/study/${encodedUid}/surfacer/decisions`,
    { withCredentials: true },
  );
  const { decisions, details } = response.data;
  if (!details) {
    // A backend without `details` would silently drop actionable/note writes.
    throw new Error("surfacer decisions response has no `details`; backend predates it");
  }
  return { decisions, details };
}

async function fetchAllDecisions(): Promise<AllDecisionsMap> {
  const baseUrl = getApiBaseUrl();
  const response = await axios.get<AllDecisionsResponse>(
    `${baseUrl}/api/reader/surfacer/decisions`,
    { withCredentials: true },
  );
  return response.data.decisions;
}

interface DecisionWrite extends DecisionDetails {
  finding: string;
  verdict: Verdict;
}

async function postDecision(cohortId: string, studyUid: string, body: DecisionWrite) {
  const baseUrl = getApiBaseUrl();
  const encodedCohort = encodeURIComponent(cohortId);
  const encodedUid = encodeURIComponent(studyUid);
  await axios.post(
    `${baseUrl}/api/reader/cohort/${encodedCohort}/study/${encodedUid}/surfacer/decision`,
    body,
    { withCredentials: true },
  );
}

/** The backend's 400 `detail` for a verdict outside its vocabulary
 * (web-demo-backend `reader_endpoints.post_study_surfacer_decision`). */
const UNSUPPORTED_VERDICT_DETAIL = "verdict must be one of";

/** Why the last write failed: the backend's `detail` when it sent one. */
function errorDetail(err: unknown): string | null {
  if (axios.isAxiosError(err)) {
    const detail: unknown = err.response?.data?.detail;
    if (typeof detail === "string") return detail;
  }
  return err instanceof Error ? err.message : null;
}

/** A failed write, for the panel to explain. `unsupportedVerdict` is set when
 * the backend rejected the verdict itself (e.g. `other_report` against a backend
 * that predates it), so retrying the same click cannot succeed. */
export interface SaveError {
  verdict: Verdict;
  detail: string | null;
  unsupportedVerdict: boolean;
}

/** Keep only the details that will be stored: a trimmed, non-empty note and a
 * defined actionable. Matches what the backend writes, so the optimistic cache
 * equals the next fetch. */
function storedDetails({ actionable, note }: DecisionDetails): DecisionDetails {
  const stored: DecisionDetails = {};
  if (actionable !== undefined) stored.actionable = actionable;
  const trimmed = note?.trim();
  if (trimmed) stored.note = trimmed;
  return stored;
}

function saveErrorOf(
  isError: boolean,
  error: unknown,
  variables: DecisionWrite | undefined,
): SaveError | null {
  if (!isError || !variables) return null;
  const detail = errorDetail(error);
  return {
    verdict: variables.verdict,
    detail,
    unsupportedVerdict:
      axios.isAxiosError(error) &&
      error.response?.status === 400 &&
      !!detail?.startsWith(UNSUPPORTED_VERDICT_DETAIL),
  };
}

export function useSurfacerDecisions(cohortId: string, studyUid: string) {
  const queryClient = useQueryClient();
  const queryKey = surfacerDecisionKeys.study(studyUid, cohortId);

  const query = useQuery({
    queryKey,
    queryFn: () => fetchDecisions(cohortId, studyUid),
    enabled: !!studyUid && !!cohortId,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const current = (): StudyDecisions =>
    queryClient.getQueryData<StudyDecisions>(queryKey) ?? { decisions: {}, details: {} };

  const mutationKey = ["surfacer-decision", cohortId, studyUid] as const;
  // A mutation still counts while its callbacks run, so 1 means no other write
  // for this study is in flight or queued behind it.
  const isLastWrite = () => queryClient.isMutating({ mutationKey }) <= 1;
  const failedSinceDrain = useRef(false);

  const mutation = useMutation({
    mutationKey,
    mutationFn: (body: DecisionWrite) => postDecision(cohortId, studyUid, body),
    scope: { id: `surfacer-decision:${cohortId}:${studyUid}` },
    onMutate: async ({ finding, verdict, actionable, note }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = current();
      const stored = storedDetails({ actionable, note });
      const details = { ...previous.details };
      delete details[finding];
      if (Object.keys(stored).length) details[finding] = stored;
      queryClient.setQueryData<StudyDecisions>(queryKey, {
        decisions: { ...previous.decisions, [finding]: verdict },
        details,
      });
      return { previous };
    },
    onError: (err, vars, context) => {
      console.error(
        `surfacer decision write failed for ${vars.finding} (${vars.verdict}); rolling back`,
        err,
      );
      failedSinceDrain.current = true;
      // A queued write already put its newer row in the cache and will still
      // send it, so restoring this write's snapshot would hide that edit.
      if (context?.previous && isLastWrite()) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      // Refresh the worklist's bulk decisions so a now-fully-reviewed case
      // drops off the list when the user returns to it.
      queryClient.invalidateQueries({ queryKey: surfacerDecisionKeys.allStudies });
      if (!isLastWrite()) return;
      // Re-read the pooled review state once the study's writes drain: the own-
      // verdict overlay only removes findings, so an undone last verdict needs the
      // server to put its finding back. Earlier, a queued write could be overtaken.
      queryClient.invalidateQueries({ queryKey: surfacerReviewStateKeys.all });
      // After a failure, re-read what the server stored; otherwise the
      // optimistic per-study cache already matches it.
      if (failedSinceDrain.current) {
        failedSinceDrain.current = false;
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  return {
    decisions: query.data?.decisions ?? {},
    details: query.data?.details ?? {},
    isLoading: query.isLoading,
    isError: mutation.isError,
    saveError: saveErrorOf(mutation.isError, mutation.error, mutation.variables),
    /** Set a verdict, keeping the finding's details only if the new verdict takes them. */
    setVerdict: (finding: string, verdict: Verdict) => {
      const kept = ACTIONABILITY_VERDICTS.has(verdict) ? current().details[finding] : undefined;
      mutation.mutate({ finding, verdict, ...storedDetails(kept ?? {}) });
    },
    /**
     * Merge `patch` over a finding's stored details and keep its verdict; a key
     * set to undefined clears it. Throws if the verdict does not take details.
     */
    setDetails: (finding: string, patch: DecisionDetails) => {
      const { decisions, details } = current();
      const verdict = decisions[finding] ?? "pending";
      if (!ACTIONABILITY_VERDICTS.has(verdict)) {
        throw new Error(
          `surfacer details need a true_miss/inconsequential verdict; got ${verdict}`,
        );
      }
      mutation.mutate({ finding, verdict, ...storedDetails({ ...details[finding], ...patch }) });
    },
  };
}

/**
 * Bulk fetch of the caller's surfacer verdicts across all studies, keyed by
 * study_uid → {finding: verdict}. One request (one DynamoDB Query) backs the
 * whole worklist — it joins this against each row's surfacer_findings to drop
 * cases whose findings have all been reviewed. Mirrors the per-user-overlay
 * pattern of useSavedCases; `enabled` gates it on auth / mode.
 */
export function useAllSurfacerDecisions(enabled: boolean) {
  return useQuery({
    queryKey: surfacerDecisionKeys.allStudies,
    queryFn: fetchAllDecisions,
    enabled,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
